import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMMUNITY_TYPES = [
  { value: "assisted_living", label: "Assisted Living" },
  { value: "independent_living", label: "Independent Living" },
  { value: "memory_care", label: "Memory Care" },
  { value: "nursing_home", label: "Nursing Home" },
  { value: "adult_day", label: "Adult Day Program" },
  { value: "other", label: "Other" },
];

const TryFree = () => {
  const [form, setForm] = useState({
    name: "", facility: "", job_title: "", email: "", phone: "", community_type: "", email_opt_in: true,
  });
  const [sending, setSending] = useState(false);
  const [trialUrl, setTrialUrl] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.community_type) {
      toast.error("Please choose your community type.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/trials/register`, {
        name: form.name,
        facility: form.facility,
        job_title: form.job_title,
        email: form.email,
        phone: form.phone || null,
        community_type: form.community_type,
        email_opt_in: form.email_opt_in,
      });
      setTrialUrl(data.trial_url);
      toast.success("Your free trial is ready!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-white border-2 border-border rounded-xl px-4 py-4 h-auto text-lg focus:ring-2 focus:ring-sage focus:border-transparent outline-none transition-all";

  return (
    <div data-testid="try-free-page" className="bg-cream">
      <Seo
        title="Free 14-Day UNSTUCK Seniors Trial for Senior Communities"
        description="Activity directors: try 14 sample UNSTUCK Seniors activity cards free for 14 days. Inclusive assisted living and nursing home activities for residents of all abilities. No payment needed."
        path="/try-free"
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-start">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-gold-dark mb-4">
              Free 14-day trial for senior communities
            </p>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-tight text-ink mb-6">
              Try UNSTUCK free for 14 days.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-ink/80 mb-6">
              Experience 14 complimentary UNSTUCK Seniors activity cards, created by an activity director
              to help residents of different abilities connect, participate, and enjoy meaningful
              15-minute activities.
            </p>
            <p className="font-heading italic text-xl text-sage mb-10">
              "Created by an Activity Director. Designed with Every Resident in Mind."
            </p>
            <ul className="space-y-3 mb-4">
              {[
                "14 sample activity cards from the 54-card Seniors deck — all available immediately",
                "Four Ways to Use UNSTUCK Seniors instructions, printable",
                "How to Use UNSTUCK Bucks reward instructions, printable",
                "Printable $1 UNSTUCK Bucks sample sheet",
                "Participation & Progress Tracker, printable",
                "Understanding Your Participation Tracker guide, printable",
                "An easy-to-use 15-minute timer",
                "A short feedback form — honest feedback only, never required to be positive",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-base text-ink/85">
                  <Check className="h-5 w-5 text-sage shrink-0 mt-0.5" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
            <p className="text-base text-ink/70">
              No payment, no card details. Access lasts 14 calendar days from registration.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-8 sm:p-10">
            {trialUrl ? (
              <div data-testid="trial-register-success" className="text-center py-6">
                <div className="h-16 w-16 rounded-full bg-sage-light flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-sage" aria-hidden="true" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-ink mb-3">Your trial is ready</h2>
                <p className="text-lg text-ink/75 mb-8">
                  We've emailed your secure trial link to <span className="font-semibold">{form.email}</span>.
                  It stays active for 14 days — bookmark it or return straight from that email.
                </p>
                <Link
                  to={new URL(trialUrl).pathname}
                  data-testid="enter-trial-button"
                  className="inline-flex items-center justify-center bg-sage text-white hover:bg-sage-dark rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
                >
                  Enter Your Free Trial
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="trial-register-form" className="space-y-5">
                <h2 className="font-heading font-bold text-2xl text-ink mb-2">Register your community</h2>
                <div>
                  <Label htmlFor="tr-name" className="text-base font-medium mb-1.5 block">Full name *</Label>
                  <Input id="tr-name" data-testid="trial-name-input" required value={form.name} onChange={update("name")} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="tr-facility" className="text-base font-medium mb-1.5 block">Facility or organization name *</Label>
                  <Input id="tr-facility" data-testid="trial-facility-input" required value={form.facility} onChange={update("facility")} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="tr-title" className="text-base font-medium mb-1.5 block">Job title *</Label>
                  <Input id="tr-title" data-testid="trial-jobtitle-input" required value={form.job_title} onChange={update("job_title")} className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="tr-email" className="text-base font-medium mb-1.5 block">Email address *</Label>
                    <Input id="tr-email" data-testid="trial-email-input" type="email" required value={form.email} onChange={update("email")} className={inputCls} />
                  </div>
                  <div>
                    <Label htmlFor="tr-phone" className="text-base font-medium mb-1.5 block">Phone (optional)</Label>
                    <Input id="tr-phone" data-testid="trial-phone-input" type="tel" value={form.phone} onChange={update("phone")} className={inputCls} />
                  </div>
                </div>
                <fieldset>
                  <legend className="text-base font-medium mb-2">Type of community *</legend>
                  <div className="grid grid-cols-2 gap-3" data-testid="trial-community-types">
                    {COMMUNITY_TYPES.map((ct) => (
                      <button
                        key={ct.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, community_type: ct.value }))}
                        aria-pressed={form.community_type === ct.value}
                        data-testid={`trial-community-${ct.value}`}
                        className={`rounded-xl px-4 py-3 text-base font-medium border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-sage ${
                          form.community_type === ct.value ? "bg-sage text-white border-sage" : "border-border text-ink hover:border-sage"
                        }`}
                      >
                        {ct.label}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.email_opt_in}
                    onChange={(e) => setForm((f) => ({ ...f, email_opt_in: e.target.checked }))}
                    data-testid="trial-email-optin"
                    className="mt-1.5 h-5 w-5 accent-[#4A5D4E]"
                  />
                  <span className="text-base text-ink/80">
                    Email me trial tips and follow-up about UNSTUCK.
                  </span>
                </label>
                <Button
                  type="submit"
                  disabled={sending}
                  data-testid="trial-register-button"
                  className="w-full bg-gold text-ink hover:bg-gold-dark hover:text-white rounded-full px-8 py-4 h-auto text-lg font-semibold transition-all duration-300"
                >
                  {sending && <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />}
                  Start My Free 14-Day Trial
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TryFree;
